import { ErrorHandler, Injectable, inject } from '@angular/core';

import { AppInsightsService } from './app-insights.service';

/** Messages browsers emit when a lazy-loaded chunk can't be fetched. Almost
 *  always a stale client after a new deploy (old chunk hashes are gone). */
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError/i;

/**
 * Forwards Angular-zone errors to Application Insights, then defers to the
 * default console behaviour. `trackException` is a no-op until the SDK is
 * initialised (Analytics consent in a configured environment).
 *
 * Special-cases lazy-chunk load failures: a fresh deploy renames the hashed
 * chunks, so an already-open tab fetching an old hash 404s. We reload once
 * (rate-limited so a genuinely-missing chunk can't loop the page) to pull the
 * current index.html + chunk names.
 */
@Injectable()
export class AppInsightsErrorHandler implements ErrorHandler {
  private readonly insights = inject(AppInsightsService);

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error)) {
      this.insights.trackEvent('chunk-load-error', { chunk: this.chunkUrl(error) });
      this.reloadOnce();
      return;
    }
    this.insights.trackException(error instanceof Error ? error : new Error(String(error)));
    // Keep the default behaviour so errors still surface in the console.
    console.error(error);
  }

  private isChunkLoadError(error: unknown): boolean {
    const msg = error instanceof Error ? error.message : String(error ?? '');
    return CHUNK_LOAD_ERROR.test(msg);
  }

  private chunkUrl(error: unknown): string {
    const msg = error instanceof Error ? error.message : String(error ?? '');
    return /(https?:\/\/\S+)/.exec(msg)?.[1] ?? '(unknown)';
  }

  /** Reload to fetch the fresh build, but at most once per 10s so a chunk that
   *  is genuinely missing (broken deploy) can't put the page in a reload loop. */
  private reloadOnce(): void {
    const KEY = 'emitroll-chunk-reload-at';
    try {
      const last = Number(sessionStorage.getItem(KEY) || '0');
      if (Date.now() - last < 10_000) { return; }
      sessionStorage.setItem(KEY, String(Date.now()));
    } catch { /* storage unavailable — fall through and reload anyway */ }
    location.reload();
  }
}
